/**
 * MultiSelectRenderer Component
 * Displays multi-select (checkbox) questions
 */

import React from 'react';
import { FormGroup, FormControlLabel, Checkbox, Box, Typography, Alert } from '@mui/material';
import type { Question } from '../../../types';
import QuestionHelpText from '../QuestionHelpText';

interface MultiSelectRendererProps {
  question: Question;
  selectedValues: string[];
  onSelect: (values: string[]) => void;
}

function MultiSelectRenderer({
  question,
  selectedValues,
  onSelect,
}: MultiSelectRendererProps): JSX.Element {
  const minSelections = (question.metadata?.minSelections as number) || 0;
  const maxSelections = (question.metadata?.maxSelections as number) || question.answers.length;

  const handleChange = (answerId: string, checked: boolean): void => {
    let newValues = [...selectedValues];

    if (checked) {
      const answer = question.answers.find(a => a.id as unknown as string === answerId);
      if (answer && answer.metadata?.noneOfTheAbove) {
        newValues = [answerId];
      } else {
        newValues = newValues.filter(id => !question.answers.find(a => a.id as unknown as string === id)?.metadata?.noneOfTheAbove);
        if (newValues.length < maxSelections) {
          newValues.push(answerId);
        }
      }
    } else {
      newValues = newValues.filter((id) => id !== answerId);
    }

    onSelect(newValues);
  };

  const ignoreIsBelow = selectedValues.some(id => question.answers.find(a => a.id as unknown as string === id)?.metadata?.noneOfTheAbove);
  const isBelowMin = selectedValues.length < minSelections && !ignoreIsBelow;
  const isAtMax = selectedValues.length >= maxSelections;

  const instructionText = minSelections > 0 && maxSelections < question.answers.length
    ? `Multi-select — choose ${minSelections} to ${maxSelections} options`
    : 'Multi-select — choose all that apply';

  return (
    <Box className="cla-quiz-multi-select" data-quiz-question-id={question.id} sx={{ mt: 1 }}>
      <QuestionHelpText description={question.metadata?.description} />

      <Typography
        className="cla-quiz-instruction"
        variant="body2"
        sx={{ mb: 2, fontStyle: 'italic', color: '#888', fontSize: '0.82rem' }}
      >
        {instructionText}
      </Typography>

      <FormGroup
        className="cla-quiz-option-group"
        sx={question.metadata?.optionsLayout === 'twoColumn' ? {
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          columnGap: 2,
        } : undefined}
      >
        {question.answers.map((answer) => {
          const isSelected = selectedValues.includes(answer.id as unknown as string);
          return (
            <FormControlLabel
              className="cla-quiz-option"
              data-quiz-answer-id={answer.id}
              key={answer.id}
              control={
                <Checkbox
                  className="cla-quiz-option-control"
                  checked={isSelected}
                  onChange={(e) => handleChange(answer.id as unknown as string, e.target.checked)}
                  disabled={isAtMax && !isSelected && !answer.metadata?.noneOfTheAbove}
                  sx={{
                    color: '#ccc',
                    '&.Mui-checked': { color: '#00A9B5' },
                  }}
                />
              }
              label={
                <Typography className="cla-quiz-option-label" variant="body1" sx={{ fontWeight: 500, color: '#1C3654' }}>
                  {answer.text}
                </Typography>
              }
              sx={{
                py: 1.5,
                px: 2,
                mb: 1.5,
                border: isSelected ? '2px solid #00A9B5' : '1.5px solid #e0e0e0',
                borderRadius: '16px',
                bgcolor: isSelected ? '#EAF8F8' : '#fff',
                mx: 0,
                transition: 'border-color 0.15s, background-color 0.15s',
                '&:hover': {
                  backgroundColor: isSelected ? '#EAF8F8' : '#f9fafb',
                  borderColor: isSelected ? '#00A9B5' : '#b0b0b0',
                },
              }}
            />
          );
        })}
      </FormGroup>

      {isBelowMin && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Please select at least {minSelections} option{minSelections > 1 ? 's' : ''}
        </Alert>
      )}
    </Box>
  );
}

export default MultiSelectRenderer;
